import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, User, Settings, LogOut } from 'lucide-react';
import logoSinTexto from '../../assets/Logo_sin_texto.png';
import { WebSocketStatus } from '../ui/WebSocketStatus';
import { OrderNotifications } from '../notifications/OrderNotifications';

interface Usuario {
  id: string;
  email: string;
  nombreCompleto: string;
  tipoUsuario: string;
  estado: boolean;
  roles?: Array<{
    id: string;
    nombre: string;
    descripcion?: string;
  }>;
}

interface DashboardHeaderProps {
  usuario?: Usuario;
  onOpenProfile?: () => void;
}

export const DashboardHeader: React.FC<DashboardHeaderProps> = ({ usuario, onOpenProfile }) => {
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Cerrar menú al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userData');
    navigate('/login');
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-lg border-b border-gray-200/60 backdrop-blur-sm">
      <div className="px-6 py-4 flex justify-between items-center">
        {/* Logo y Título */}
        <div className="flex items-center space-x-4">
          <img
            src={logoSinTexto}
            alt="ConfiFarma Amiga"
            className="w-12 h-12 object-contain rounded-xl shadow-lg"
          />
          <div>
            <h1 className="text-xl font-bold text-gray-800">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#4CAF50] to-[#2196F3]">ConfiFarma</span> <span className="text-[#2196F3]">Amiga</span>
            </h1>
            <p className="text-sm text-gray-500 font-normal">
              Tu salud con confianza
            </p>
          </div>
        </div>

        {/* Información del usuario y acciones */}
        <div className="flex items-center space-x-6">
          {/* Estado WebSocket */}
          <div className="p-2">
            <WebSocketStatus showLabel={false} />
          </div>
          
          {/* Notificaciones de pedidos */}
          <OrderNotifications />

          {/* Información del usuario con dropdown */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center space-x-3 p-2 rounded-xl hover:bg-[#4CAF50]/10 transition-all duration-200 group"
            >
              <div className="text-right">
                <p className="text-sm font-medium text-gray-700 group-hover:text-[#4CAF50] transition-colors">
                  {usuario?.nombreCompleto || 'Usuario'}
                </p>
                <p className="text-xs text-[#4CAF50] capitalize font-normal">
                  {usuario?.roles?.map(r => r.nombre).join(', ') || usuario?.tipoUsuario || 'Invitado'}
                </p>
              </div>
              
              <div className="relative">
                <div className="w-10 h-10 bg-gradient-to-br from-[#4CAF50] to-[#2196F3] rounded-xl flex items-center justify-center shadow-md group-hover:shadow-lg transition-all duration-200">
                  <span className="text-white text-sm font-medium">
                    {usuario?.nombreCompleto?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-[#4CAF50] rounded-full ring-2 ring-white"></div>
              </div>
              
              <ChevronDown className={`w-4 h-4 text-gray-400 group-hover:text-[#4CAF50] transition-all duration-300 ${
                showUserMenu ? 'rotate-180' : ''
              }`} />
            </button>

            {/* Dropdown Menu */}
            <div className={`absolute right-0 mt-4 w-96 bg-white rounded-2xl shadow-xl border border-gray-200/60 py-5 z-50 backdrop-blur-sm transform transition-all duration-300 ease-in-out origin-top-right ${
              showUserMenu 
                ? 'opacity-100 scale-100 translate-y-0' 
                : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'
            }`}>
              {/* User Info Section */}
              <div className="px-5 py-4 border-b border-gray-100">
                <div className="flex items-center space-x-3">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#4CAF50] to-[#2196F3] rounded-xl flex items-center justify-center shadow-lg">
                    <span className="text-white font-medium text-lg">
                      {usuario?.nombreCompleto?.charAt(0).toUpperCase() || 'U'}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-700 truncate">
                      {usuario?.nombreCompleto || 'Usuario'}
                    </p>
                    <p className="text-sm text-gray-600 truncate">{usuario?.email}</p>
                    <p className="text-xs text-[#4CAF50] capitalize font-medium mt-1 truncate">
                      {usuario?.roles?.map(r => r.nombre).join(', ') || usuario?.tipoUsuario || 'Invitado'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Menu Items */}
              <div className="py-3">
                <button 
                  onClick={() => {
                    setShowUserMenu(false);
                    onOpenProfile?.();
                  }}
                  className="w-full px-5 py-3.5 text-left hover:bg-[#4CAF50]/10 transition-colors duration-200 flex items-center space-x-3 text-gray-700 hover:text-[#4CAF50]"
                >
                  <User className="w-5 h-5" />
                  <span className="text-sm font-medium">Mi Perfil</span>
                </button>

                <div className="h-px bg-gray-100 my-2 mx-5"></div>
                
                <button
                  onClick={handleLogout}
                  className="w-full px-5 py-3.5 text-left hover:bg-red-50 transition-colors duration-200 flex items-center space-x-3 text-red-600 hover:text-red-700"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="text-sm font-medium">Cerrar Sesión</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};
